import { AxiosResponse } from 'axios'

// realisations
import { http } from '../realisations/Http.realisation'

// types
import {
    BusinessModelsGetListResponseT,
    CompaniesCreateCompanyResponseT,
    CompaniesDownloadExcelPayloadT,
    CompaniesDownloadExcelResponseT,
    CompaniesGenerateContractResponseT,
    CompaniesGetActivityResponse,
    CompaniesGetAgreementBasesResponseT,
    CompaniesGetAllListParamsT,
    CompaniesGetCompanyResponseT,
    CompaniesGetCompanySettingsParams,
    CompaniesGetCompanySettingsResponse,
    CompaniesGetCompanySourcesResponse,
    CompaniesGetCountriesAndManagersResponseT,
    CompaniesGetCountriesResponseT,
    CompaniesGetListBillingParams,
    CompaniesGetListParams,
    CompaniesGetListResponseT,
    CompaniesGetPeriodsResponseT,
    CompaniesGetSchedulesResponseT,
    CompaniesGetStagesResponseT,
    CompaniesGetSubdivisionsResponseT,
    CompaniesGetTariffsResponse,
    CompaniesGetTransportTypesResponseT,
    CompaniesGetWeightTypesResponseT,
    CompaniesUpdateCompanyResponseT,
    CompanyGenerateContractPayloadT,
    CompanyGetOrderTypesResponse,
    CompanyGetTranscriptTypesResponse,
    CompanyPayloadT,
    CompanySetBillingPayloadT,
    CompanySetClientPayloadT,
    GetCompaniesParams,
    GetCompanyContractsResponse,
    GetPayersResponseT,
} from '../types/companies.types'

export class CompaniesRepository {
    getAllList(
        params: CompaniesGetAllListParamsT
    ): Promise<AxiosResponse<CompaniesGetListResponseT>> {
        return http.get('/cabinet/api/v2/companies', { params })
    }

    getCompanies(params: GetCompaniesParams): Promise<AxiosResponse<CompaniesGetListResponseT>> {
        return http.get('/cabinet/api/v2/companies', {
            params: {
                ...params,
                statusId: 1,
            },
        })
    }

    getTransportTypes(): Promise<AxiosResponse<CompaniesGetTransportTypesResponseT>> {
        return http.get('/cabinet/api/companies/transport-types', {})
    }

    getCompany(company_id: number): Promise<AxiosResponse<CompaniesGetCompanyResponseT>> {
        return http.get(`/cabinet/api/v2/companies/${company_id}`)
    }

    getCompanySettings(
        company_id: number,
        params: CompaniesGetCompanySettingsParams
    ): Promise<AxiosResponse<CompaniesGetCompanySettingsResponse>> {
        return http.get(`/cabinet/api/companies/${company_id}/company-settings`, {
            params,
        })
    }

    getList(params: CompaniesGetListParams): Promise<AxiosResponse<CompaniesGetListResponseT>> {
        return http.get('/cabinet/api/v2/companies', { params })
    }

    getListBilling(
        params: CompaniesGetListBillingParams
    ): Promise<AxiosResponse<CompaniesGetListResponseT>> {
        return http.get('/cabinet/api/v2/companies', {
            params: {
                ...params,
                statusId: 2,
            },
        })
    }

    getSubdivisions(params: any): Promise<AxiosResponse<CompaniesGetSubdivisionsResponseT>> {
        return http.get('/cabinet/api/v2/companies/subdivisions', { params })
    }

    getCountriesAndManagers(): Promise<AxiosResponse<CompaniesGetCountriesAndManagersResponseT>> {
        return http.get('/cabinet/api/company/create')
    }

    getCountries(
        page: number,
        search?: string
    ): Promise<AxiosResponse<CompaniesGetCountriesResponseT>> {
        const params = {
            page,
            search,
        }

        return http.get('/cabinet/api/v2/countries', { params })
    }

    createCompany(
        company_payload: CompanyPayloadT
    ): Promise<AxiosResponse<CompaniesCreateCompanyResponseT>> {
        return http.post('/cabinet/api/v2/companies', company_payload)
    }

    setClient(payload: CompanySetClientPayloadT): Promise<AxiosResponse<void>> {
        return http.post(`/cabinet/api/companies/${payload.id}/set-client-email`, payload)
    }

    getPayers(page: number, company_id: number): Promise<AxiosResponse<GetPayersResponseT>> {
        const params = {
            page,
        }

        return http.get(`/cabinet/api/companies/payer-companies/${company_id}`, { params })
    }

    sendPayers(company_id: number, payer_company_data: any[]): Promise<AxiosResponse<void>> {
        return http.post('/cabinet/api/companies/payer-companies', {
            company_id,
            payer_company_data,
        })
    }

    resetPassword(email: string, password: string): Promise<AxiosResponse<void>> {
        return http.post('/gateway/users/change-password-by', {
            email,
            password,
        })
    }

    changeManager(oldManager: number, newManager: number): Promise<AxiosResponse<void>> {
        return http.put(`/cabinet/api/v2/companies/change-manager`, { oldManager, newManager })
    }

    changeManagerByExcel(form_data: FormData): Promise<AxiosResponse<void>> {
        return http.postFormData('/cabinet/api/v2/companies/change-managers-by-excel', form_data)
    }

    updateCompany(
        company_id: number,
        company_payload: CompanyPayloadT
    ): Promise<AxiosResponse<CompaniesUpdateCompanyResponseT>> {
        return http.put(`/cabinet/api/v2/companies/${company_id}`, company_payload)
    }

    enableVerification(company_id: number, type_id: number): Promise<AxiosResponse<void>> {
        return http.put(`/cabinet/api/v2/companies/${company_id}/verify`, {
            id: company_id,
            verify: type_id,
        })
    }

    setBilling(
        company_id: number,
        params: CompanySetBillingPayloadT
    ): Promise<AxiosResponse<void>> {
        return http.put(`/cabinet/api/v2/companies/${company_id}/billing-settings`, params)
    }

    disableVerification(company_id: number): Promise<AxiosResponse<void>> {
        return http.put(`/cabinet/api/v2/companies/${company_id}/unverify`, { id: company_id })
    }

    activateCompany(company_id: number): Promise<AxiosResponse<void>> {
        return http.put(`/cabinet/api/v2/companies/${company_id}/active`, {})
    }

    enableMassApprove(company_id: number): Promise<AxiosResponse<void>> {
        return http.put(`/cabinet/api/v2/companies/${company_id}/enable-mass-approve`, {})
    }

    disableMassApprove(company_id: number): Promise<AxiosResponse<void>> {
        return http.put(`/cabinet/api/v2/companies/${company_id}/disable-mass-approve`, {})
    }

    confirmCarrier(id: number): Promise<AxiosResponse<void>> {
        return http.post(`/cabinet/api/v2/companies/${id}/confirm`, { id })
    }

    deactivateCompany(company_id: number): Promise<AxiosResponse<void>> {
        return http.put(`/cabinet/api/v2/companies/${company_id}/deactivate`, { id: company_id })
    }

    generateContract(
        company_id: number,
        contract_payload: CompanyGenerateContractPayloadT
    ): Promise<AxiosResponse<CompaniesGenerateContractResponseT>> {
        const params = {
            fullNameInitials: contract_payload.fullname_initials,
            base: contract_payload.base,
        }

        return http.get(`/cabinet/api/v2/companies/${company_id}/agreement`, {
            responseType: 'blob',
            params,
        })
    }

    getCompanyBases(): Promise<AxiosResponse<CompaniesGetAgreementBasesResponseT>> {
        return http.get('/cabinet/api/company/agreement/bases')
    }

    getBusinessModels(): Promise<AxiosResponse<BusinessModelsGetListResponseT>> {
        return http.get('/cabinet/api/v2/company/business-models')
    }

    activateCompanySetting(company_id: number, setting_id: number): Promise<AxiosResponse<void>> {
        return http.put(
            `/cabinet/api/companies/${company_id}/company-settings/${setting_id}/active`,
            {}
        )
    }

    disableCompanySetting(company_id: number, setting_id: number): Promise<AxiosResponse<void>> {
        return http.put(
            `/cabinet/api/companies/${company_id}/company-settings/${setting_id}/inactive`,
            {}
        )
    }

    resendCompanyContract(company_id: number): Promise<AxiosResponse<void>> {
        return http.post(`/cabinet/api/companies/resend-company-contract/${company_id}`, {})
    }

    resendCompanyTo1C(company_id: number): Promise<AxiosResponse<void>> {
        return http.post(`/cabinet/api/v2/companies/${company_id}/resend`, {})
    }

    sendToContract(company_id: number): Promise<AxiosResponse<void>> {
        return http.post(`/cabinet/api/v2/courier-supplier/${company_id}/send-to-contract`, {})
    }

    getSchedules(params: any): Promise<AxiosResponse<CompaniesGetSchedulesResponseT>> {
        return http.get('/cabinet/api/companies/schedule', { params })
    }

    getActivity(
        companyId: number,
        createdAtFrom?: string | null,
        createdAtTo?: string | null
    ): Promise<AxiosResponse<CompaniesGetActivityResponse>> {
        return http.get('/analytics/api/company-activity', {
            params: { companyId, createdAtFrom, createdAtTo },
        })
    }

    getStages(page: number): Promise<AxiosResponse<CompaniesGetStagesResponseT>> {
        const params = { page }

        return http.get('/cabinet/api/dictionaries/billing-payment-stages', { params })
    }

    getPeriods(page: number): Promise<AxiosResponse<CompaniesGetPeriodsResponseT>> {
        const params = { page }

        return http.get('/cabinet/api/billing-periods', { params })
    }

    getWeightTypes(page: number): Promise<AxiosResponse<CompaniesGetWeightTypesResponseT>> {
        const params = { page }

        return http.get('/cabinet/api/dictionaries/billing-weight-types', { params })
    }

    getTariffs(): Promise<AxiosResponse<CompaniesGetTariffsResponse>> {
        return http.get('/cabinet/api/v2/company/shipment-tariffs')
    }

    getOrderTypes(): Promise<AxiosResponse<CompanyGetOrderTypesResponse>> {
        return http.get('/cabinet/api/dictionaries/billing-order-types', {
            params: {
                limit: 9999,
            },
        })
    }

    getTranscriptTypes(): Promise<AxiosResponse<CompanyGetTranscriptTypesResponse>> {
        return http.get('/cabinet/api/dictionaries/billing-transcript-types', {
            params: {
                limit: 9999,
            },
        })
    }

    getCompanySources(): Promise<AxiosResponse<CompaniesGetCompanySourcesResponse>> {
        return http.get('/cabinet/api/v2/company/company-sources')
    }

    exportExcel(
        params: CompaniesDownloadExcelPayloadT
    ): Promise<AxiosResponse<CompaniesDownloadExcelResponseT>> {
        return http.get('/cabinet/api/v2/companies/export', {
            responseType: 'blob',
            params: { ...params },
        })
    }

    importCompanies(payload: FormData): Promise<AxiosResponse<void>> {
        return http.post('/cabinet/api/v2/companies/import', payload)
    }

    enableSetting(companyId: number, settingId: number): Promise<AxiosResponse<void>> {
        return http.put(
            `/cabinet/api/companies/${companyId}/company-settings/${settingId}/active`,
            { settingId }
        )
    }

    disableSetting(companyId: number, settingId: number): Promise<AxiosResponse<void>> {
        return http.put(
            `/cabinet/api/companies/${companyId}/company-settings/${settingId}/inactive`,
            { id: companyId, settingId }
        )
    }

    getCompanyContracts(companyId: number): Promise<AxiosResponse<GetCompanyContractsResponse>> {
        return http.get('/contract/api/contract', {
            params: {
                company_id: companyId,
                status_ids: [1, 3],
                type_ids: [2]
            }
        })
    }
}
